import { Query } from '@apollo/client/react/components';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import { Desktop } from '@99designs/frontend-components/components/Desktop';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { Mobile } from '@99designs/frontend-components/components/Mobile';
import { RenderOnClient } from '@99designs/frontend-components/components/RenderOnClient';
import { ScrollableDropdown } from '../ScrollableDropdown/ScrollableDropdown';
import { ScrollableDropdownContent } from '../ScrollableDropdown/ScrollableDropdownContent';
import { RecentChats, RecentChatsVariables } from './__generated__/RecentChats';
import { OnsiteMessagesIcon } from './components/OnsiteMessagesIcon/OnsiteMessagesIcon';
import { OnsiteMessagesList } from './components/OnsiteMessagesList/OnsiteMessagesList';

export const RECENT_CHAT_QUERY = gql`
    query RecentChats($from: Int!, $limit: Int!) {
        recentChats(from: $from, limit: $limit) {
            id
            participants {
                id
                user {
                    id
                    displayName
                    penaltyState
                }
                lastReadAt
            }
            lastEvent {
                id
                author {
                    id
                    displayName
                }
                createdAt
                content
            }
        }
        viewer {
            id
            displayName
            isDesigner
            penaltyState
        }
    }
`;

export const defaultUrls = {
    messages: '/messages',
};

export interface OnsiteMessagesProps {
    urls: typeof defaultUrls;
}
export interface OnsiteMessagesState {
    isFirstRender: boolean;
    hasError: boolean;
}

export class OnsiteMessages extends React.Component<OnsiteMessagesProps, OnsiteMessagesState> {
    readonly state: OnsiteMessagesState = {
        isFirstRender: true,
        hasError: false,
    };

    render() {
        const { urls } = this.props;
        const { isFirstRender, hasError } = this.state;
        const iconFallback = (
            <a
                className="top-level-nav__item__link top-level-nav__item--onsite-messages-icon"
                href={urls.messages}
            >
                <Icon type="mail-4" variations={['inline']} />
            </a>
        );
        const icon = <OnsiteMessagesIcon />;

        return (
            <div className="onsite-messages">
                <RenderOnClient fallback={iconFallback}>
                    <Mobile>
                        <a
                            className="top-level-nav__item__link top-level-nav__item--onsite-messages-icon"
                            href={urls.messages}
                        >
                            {icon}
                        </a>
                    </Mobile>
                    <Desktop>
                        <ScrollableDropdown
                            url={urls.messages}
                            label={
                                <div className="top-level-nav__item__link top-level-nav__item--onsite-messages-icon">
                                    {icon}
                                </div>
                            }
                            popperModifiers={{ offset: { enabled: true, offset: '90px, 12px' } }}
                        >
                            <Query<RecentChats, RecentChatsVariables>
                                query={RECENT_CHAT_QUERY}
                                variables={{ from: 0, limit: 30 }}
                                notifyOnNetworkStatusChange
                                onCompleted={() => {
                                    if (hasError) {
                                        this.setState({ isFirstRender: true, hasError: false });
                                    }
                                }}
                                onError={() => {
                                    if (isFirstRender) {
                                        this.setState({ isFirstRender: false, hasError: true });
                                    }
                                }}
                                fetchPolicy="cache-and-network"
                            >
                                {(queryResult) => {
                                    const { error, loading, data } = queryResult;
                                    return (
                                        <ScrollableDropdownContent
                                            leftHeaderContent={
                                                <FormattedMessage
                                                    id="Messages"
                                                    defaultMessage="Messages"
                                                />
                                            }
                                            leftFooterContent={
                                                !(hasError || error || loading) &&
                                                data &&
                                                data.recentChats.length > 0 && (
                                                    <a
                                                        href={urls.messages}
                                                        className="scrollable-dropdown__link"
                                                    >
                                                        <FormattedMessage
                                                            id="See all messages"
                                                            defaultMessage="See all messages"
                                                        />
                                                    </a>
                                                )
                                            }
                                        >
                                            <OnsiteMessagesList queryResult={queryResult} />
                                        </ScrollableDropdownContent>
                                    );
                                }}
                            </Query>
                        </ScrollableDropdown>
                    </Desktop>
                </RenderOnClient>
            </div>
        );
    }
}
export default OnsiteMessages;
